import { Component, inject, Input } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from 'src/app/core/services/notification.service';
import { SelectModule } from 'primeng/select';
import {
  ImpactChartOptions,
  ImpactGraphType,
} from '../models/pdf-options.models';
import type {
  ChartLogoPosition,
  ChartLogoSource,
} from 'src/app/impact/models/chart-logo.model';
import { environment } from 'src/environments/environment';
import { SelectButtonModule } from 'primeng/selectbutton';
import { LockedButtonComponent } from '../components/locked-button/locked-button.component';
import { PackLimitationsService } from '../services/pack-limitations.service';
import { LimitedFeatures } from '../models/pack.models';

@Component({
  selector: 'app-pdf',
  standalone: true,
  imports: [
    ButtonModule,
    DialogModule,
    SelectModule,
    CheckboxModule,
    FormsModule,
    TranslocoPipe,
    SelectButtonModule,
    LockedButtonComponent,
  ],
  templateUrl: './pdf.component.html',
})
export class PdfComponent {
  @Input() tripId!: string;
  @Input() title!: string;
  /** Company/client logos selectable for the charts. Empty hides the logo section. */
  @Input() logoSources: ChartLogoSource[] = [];

  notificationService = inject(NotificationService);
  translocoService = inject(TranslocoService);
  http = inject(HttpClient);
  packLimitationService = inject(PackLimitationsService);

  loading = false;
  showDialog = false;
  canExportPdf = this.packLimitationService.canExportPDF;
  limitedFeature = LimitedFeatures.Pdf;

  // Valeurs disponibles
  languages = [
    { label: 'Français', value: 'fr' },
    { label: 'English', value: 'en' },
    { label: 'Nederlands', value: 'nl' },
  ];

  graphsOptions = Object.values(ImpactGraphType);

  logoPositionOptions: Array<{ label: string; value: ChartLogoPosition }> = [
    {
      label: this.translocoService.translate('impact.overview.logo.positionNone'),
      value: 'none',
    },
    {
      label: this.translocoService.translate(
        'impact.overview.logo.positionCenter',
      ),
      value: 'center',
    },
    {
      label: this.translocoService.translate('impact.overview.logo.positionSide'),
      value: 'side',
    },
  ];

  // Options sélectionnées par défaut
  options: ImpactChartOptions = {
    lang: this.translocoService.getActiveLang(),
    perTraveler: false,
    selectedGraphs: Object.values(ImpactGraphType),
    logoPosition: 'none',
  };

  openDialog() {
    // Source implicite quand il n'y en a qu'une (le select n'est alors pas affiché)
    const isSelectionValid = this.logoSources.some(
      (source) => source.id === this.options.logoSourceId,
    );
    if (!isSelectionValid) {
      this.options.logoSourceId = this.logoSources[0]?.id;
    }
    if (this.logoSources.length === 0) {
      this.options.logoPosition = 'none';
    }
    this.showDialog = true;
  }

  download() {
    this.loading = true;

    this.options.perTraveler = this.options.perTraveler ?? true;
    this.http
      .post(`${environment.apiUrl}/pdf/${this.tripId}`, this.options, {
        responseType: 'blob',
      })
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const baseFilename = this.translocoService.translate('pdf.filename');
          const filename = `${baseFilename}_${this.title}_${this.options.lang}.pdf`;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
          this.loading = false;
          this.showDialog = false;
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error(
            this.translocoService.translate('pdf.error'),
          );
          this.loading = false;
          this.showDialog = false;
        },
      });
  }
}
